/**
 * PostHog instrumentation for global search — one vocabulary for the navbar
 * dropdown and the results page so CTR and zero-result rates can be computed
 * per surface and per section.
 *
 * Events (all snake_case, all carry `search_id` so they can be joined):
 *
 *   search_performed      — a query landed on the results page.
 *   search_results_shown  — a section's first page loaded (`count` may be 0).
 *                           The dropdown fires one event with `section: "all"`
 *                           plus a `sections` map of per-section counts.
 *   search_result_clicked — any result was clicked; `section` says which list,
 *                           `surface` says where.
 *
 * Deriving the metrics (see docs/site-search-index.md for the HogQL):
 *   CTR                 = searches with ≥1 click / searches
 *   zero-result rate    = results_shown with count 0 / results_shown
 *   no-click searches   = search_performed without a matching clicked event
 *
 * Every helper is a no-op when PostHog is unavailable — analytics never throws.
 */

export const SEARCH_EVENTS = {
  PERFORMED: "search_performed",
  RESULTS_SHOWN: "search_results_shown",
  RESULT_CLICKED: "search_result_clicked",
};

export const SEARCH_SURFACE = {
  DROPDOWN: "dropdown",
  RESULTS_STRIP: "results_strip",
  RESULTS_TAB: "results_tab",
};

/** Result lists, named after what the user sees. */
export const SEARCH_SECTION = {
  SERVICES: "services",
  MENTORS: "mentors",
  QUESTS: "quests",
  COMMUNITIES: "communities",
  MEMBERS: "members",
  APPS: "apps",
  PAGES: "pages",
  ARTICLES: "articles",
  ALL: "all",
};

/** Site-page category (`app | page | article`) → section name. */
export const SECTION_BY_SITE_PAGE_CATEGORY = {
  app: SEARCH_SECTION.APPS,
  page: SEARCH_SECTION.PAGES,
  article: SEARCH_SECTION.ARTICLES,
};

/** Unique id per search so shown/clicked events can be joined to it. */
export function makeSearchId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch {
    // crypto unavailable — fall through.
  }
  return `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function captureSearchPerformed(posthog, { searchId, query }) {
  if (!query) return;
  posthog?.capture(SEARCH_EVENTS.PERFORMED, {
    search_id: searchId,
    query,
  });
}

export function captureResultsShown(
  posthog,
  { searchId, query, surface, section, count, sections }
) {
  posthog?.capture(SEARCH_EVENTS.RESULTS_SHOWN, {
    search_id: searchId,
    query,
    surface,
    section,
    count,
    has_results: count > 0,
    ...(sections && { sections }),
  });
}

export function captureResultClicked(
  posthog,
  { searchId, query, surface, section, position, result }
) {
  posthog?.capture(SEARCH_EVENTS.RESULT_CLICKED, {
    search_id: searchId,
    query,
    surface,
    section,
    position,
    result_id: result?.id ?? result?._id ?? null,
    title:
      result?.title ?? result?.description ?? result?.displayName ?? result?.questText ?? null,
    url: result?.url ?? null,
    category: result?.category ?? null,
  });
}

/**
 * Total hits across the dropdown's sections. Users and mentors arrive as
 * id lists; everything else as document arrays.
 */
export function countDropdownSections(searchBarResults) {
  const sections = {};
  let total = 0;
  for (const [key, value] of Object.entries(searchBarResults || {})) {
    const count = Array.isArray(value) ? value.length : 0;
    sections[key] = count;
    total += count;
  }
  return { sections, total };
}
